<template>
  <form
    :id="id <= 0 ? 'comment-form' : `comment-form-${id}`"
    action="post"
    class="comment__form"
    @submit.prevent="submit"
  >
    <div class="discussion-comment form__group" :class="{ error: errors.has('comment') }">
      <textarea
        ref="textarea"
        v-model="comment"
        :style="textareaStyles"
        name="comment"
        placeholder="Add your response"
        data-vv-as="Comment"
        aria-label="Comment"
        @input="resizeTextarea"
        @focus="handleFocus()"
      />
      <input-error input-id="comment" />
    </div>

    <div class="discussion-image form__group" :class="{ error: errors.has(imageIdName) }">
      <div class="discussion-image-upload">
        <div class="file-input">
          <span v-if="!hasValidImage" class="iconfont-image" />
          <label v-if="!hasValidImage" class="image" :for="imageIdName"
            ><strong>Attach an Image</strong> (10mb limit)</label
          >
          <input
            :id="imageIdName"
            ref="fileInput"
            v-validate="'mimes:image/png,image/jpeg,image/gif|size:10240'"
            class="form-control"
            :name="imageIdName"
            type="file"
            :accept="mimeTypes.join(',')"
            data-vv-as="Image"
            data-vv-validate-on="change|submit"
            @change="onFileChange"
          />
        </div>
        <div v-if="hasValidImage" class="file-remove">
          <button class="btn -image-remove" title="Remove Image" @click.prevent="removeFile">
            <span class="iconfont-menu-close" />Remove Image
          </button>
          <img :src="imageDataString" alt="" />
        </div>
      </div>
      <input-error :input-id="imageIdName" />
    </div>
    <div class="action__group">
      <button class="btn -main -large-medium-only" :disabled="processing">
        Reply
      </button>
      <!-- Not currently shown  -->
      <button class="sr-only" @click.prevent="cancel">
        Cancel
      </button>
    </div>
  </form>
</template>

<script>
// @flow
import InputError from '../forms/input-error.vue'

const MINIMUM_TEXTAREA_HEIGHT = 60

export default {
  components: { InputError },
  props: {
    id: {
      type: Number,
      default: -1,
    },
    handleFocus: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      // form processing state
      processing: false,
      comment: '',
      // the actual image file as a string, after the file is selected
      imageDataString: '',
      // the form file object, used for submitting
      imageFile: null,
      textareaStyles: 'word-wrap: break-word;',
      mimeTypes: ['image/png', 'image/jpeg', 'image/gif'],
    }
  },
  computed: {
    imageIdName() {
      return this.id <= 0 ? 'image-field' : `image-field-${this.id}`
    },
    hasValidImage() {
      return !this.errors.has(this.imageIdName) && this.imageDataString // && !this.errors.has('image')
    },
  },
  created() {
    // custom validation messages for comment images
    this.$validator.updateDictionary({
      en: {
        messages: {
          mimes: `Only ${this.mimeTypes.join(', ').replace(/image\//g, '')} supported`,
          size: 'Must be an image under 10MB',
        },
      },
    })
  },
  methods: {
    cancel() {
      this.$store.commit('closeCommentForm')
    },

    async submit() {
      if (!this.processing) {
        this.processing = true

        try {
          // must have comment OR image OR both to proceed
          if (!this.comment.trim() && !this.imageFile) {
            this.$validator.errorBag.add('comment', 'Comment or image required')
            this.processing = false
            return
          }

          // remove comment / image error
          this.$validator.errorBag.remove('comment')

          // any other validation errors
          const result = await this.$validator.validateAll()

          // success
          if (result) {
            this.$store.dispatch('submitComment', {
              body: this.comment,
              imageFile: this.imageFile,
              // imageDataString: this.imageDataString,
            })

            // // note: form values cleared out before successful response from server
            this.comment = ''
            this.removeFile()
          }

          // reset form state
          this.processing = false
        } catch (e) {
          // failed, reset form state
          console.warn('error processing comment form')
          this.processing = false
        }
      }

      // if (this.comment === '') {
      //   return
      // }
      //
      // this.$store.dispatch('submitComment', {
      //   body: this.comment,
      //   imageFile: this.imageFile,
      //   imageDataString: this.imageDataString,
      // })
      //
      // // note: form values cleared out before successful response from server
      // this.comment = ''
      // this.removeFile()
    },

    // TODO: make a new resize text area component, see comment-edit as it is duplicated
    resizeTextarea() {
      const { textarea } = this.$refs
      const initialScrollHeight = textarea.scrollHeight
      const initialTextareaHeight = parseInt(textarea.style.height)
      // @TODO make MINIMUM_TEXTAREA_HEIGHT dynamic
      if (
        initialScrollHeight < initialTextareaHeight &&
        initialScrollHeight >= MINIMUM_TEXTAREA_HEIGHT
      ) {
        textarea.style.height = '0px'
      }
      // Cap @ 8 lines
      // @TODO make this dynamic as well...
      if (textarea.scrollHeight < 280) {
        textarea.style.height = `${Math.max(textarea.scrollHeight, MINIMUM_TEXTAREA_HEIGHT)}px`
      }
    },

    onFileChange(e: any) {
      const target = e.target || e.dataTransfer
      const files = target && target.files

      if (!files.length || this.errors.has(this.imageIdName)) {
        return
      }

      // do not render if not supported image
      if (!this.mimeTypes.includes(files[0].type)) {
        return
      }

      this.createImage(files[0])
    },

    // display image inline ONLY if no error
    createImage(file: any) {
      this.imageFile = file

      const reader = new window.FileReader()

      reader.onload = e => {
        this.imageDataString = e.target.result
      }

      reader.readAsDataURL(file)
    },

    removeFile() {
      this.$refs.fileInput.value = ''
      this.imageDataString = ''
      this.imageFile = null
    },
  },
}
</script>
